import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  MARQUES, MARQUES_PHONAK_GROUP, MARQUES_OTICON_GROUP, MARQUES_WSA_GROUP,
  EMBOUT_TYPES, OBTURATEUR_TYPES, MONTAGE_MESSAGES, MONTAGE_LABELS,
  INTEGRAL_VERSIONS, INTEGRAL_PUISSANCES, INTEGRAL_CABLE_LENGTHS_50, INTEGRAL_CABLE_LENGTHS_60
} from './orderFormConstants';
import { OrangeInfoBox, ImagePlaceholder } from './OrderFormHelpers';

export default function FormEmboutDetails({ form, compact, idPrefix, changedFields }) {
  const id = (name) => idPrefix ? `${idPrefix}-${name}` : name;
  const labelClass = compact ? 'label-overline mb-1 block' : 'label-overline mb-2 block';
  const sectionClass = compact
    ? 'bg-white border border-zinc-200 p-3 sm:p-4 rounded-sm'
    : 'bg-white border border-zinc-200 p-4 sm:p-6';
  const headingClass = compact
    ? 'font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200'
    : 'font-heading font-semibold text-base sm:text-lg mb-4 pb-2 border-b border-zinc-200';
  const gapClass = compact ? 'gap-3' : 'gap-4';
  const H = compact ? 'h3' : 'h2';
  const isChanged = (fieldKey) => changedFields && changedFields.has(fieldKey);
  const changedBorder = (fieldKey) => isChanged(fieldKey) ? 'ring-2 ring-red-500 border-red-400' : '';

  const isRIC = form.emboutType === 'ecouteur_deporte';
  const isIntegral = form.emboutType === 'integral';
  const isPhonakGroup = MARQUES_PHONAK_GROUP.includes(form.marque);
  const isOticonGroup = MARQUES_OTICON_GROUP.includes(form.marque);
  const isWSAGroup = MARQUES_WSA_GROUP.includes(form.marque);
  const cableOptions = form.ecouteurVersion === '6.0'
    ? INTEGRAL_CABLE_LENGTHS_60
    : INTEGRAL_CABLE_LENGTHS_50;

  return (
    <>
      {/* Type de fabrication */}
      <div className={sectionClass}>
        <H className={headingClass}>Type de fabrication</H>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${gapClass}`}>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label className={labelClass}>Fabrication *</Label>
              <Select value={form.fabricationType} onValueChange={form.setFabricationType}>
                <SelectTrigger className="rounded-sm bg-white" data-testid="fabrication-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="embout_auditif">Embout auditif</SelectItem>
                  <SelectItem value="obturateur">Obturateur</SelectItem>
                  <SelectItem value="conformateur_oreille">Conformateur d'oreille</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Obturateur type selector */}
          {form.fabricationType === 'obturateur' && (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className={labelClass}>Type d'obturateur *</Label>
                <Select value={form.obturateurType} onValueChange={form.setObturateurType}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="obturateur-type-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {OBTURATEUR_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.obturateurType && (() => {
                // Pour les obturateurs filtrés (anti-bruit filtré + bouchon musique),
                // l'image dépend aussi de la forme choisie (canule vs fond de conque).
                const isFiltered = form.obturateurType === 'anti_bruit_filtre' || form.obturateurType === 'bouchon_musique';
                let src;
                if (isFiltered) {
                  src = form.obturateurForme === 'canule'
                    ? '/images/formes/filtre_canule.png'
                    : '/images/formes/filtre_fond_conque.png';
                } else {
                  const imgMap = {
                    anti_eau: '/images/formes/obturateur_poignee.png',
                    anti_bruit_non_filtre: '/images/formes/antibruit_non_filtre.png',
                  };
                  src = imgMap[form.obturateurType];
                }
                if (!src) return null;
                return (
                  <div className="w-20 h-20 rounded-sm overflow-hidden border border-zinc-200 flex-shrink-0 bg-zinc-50" data-testid="obturateur-img">
                    <img src={src} alt={form.obturateurType} className="w-full h-full object-contain" />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Embout auditif details */}
          {form.fabricationType === 'embout_auditif' && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className={labelClass}>Type d'embout auditif *</Label>
                  <Select value={form.emboutType} onValueChange={form.setEmboutType}>
                    <SelectTrigger className="rounded-sm bg-white" data-testid="embout-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {EMBOUT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <ImagePlaceholder label={form.emboutType} />
              </div>

              {isRIC && (
                <>
                  <div>
                    <Label className={`${labelClass} ${isChanged('marque') ? 'text-red-600 font-bold' : ''}`}>Marque de l'appareil *</Label>
                    <Select value={form.marque} onValueChange={form.setMarque}>
                      <SelectTrigger className={`rounded-sm bg-white ${changedBorder('marque')}`} data-testid="marque-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent className="bg-white">{MARQUES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {/* Phonak group */}
                  {form.marque && isPhonakGroup && (
                    <>
                      <div>
                        <Label className={`${labelClass} ${isChanged('ecouteur_version') ? 'text-red-600 font-bold' : ''}`}>Modèle d'écouteur *</Label>
                        <Select value={form.ecouteurVersion} onValueChange={form.setEcouteurVersion}>
                          <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_version')}`} data-testid="ecouteur-version-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-white"><SelectItem value="5.0">5.0</SelectItem><SelectItem value="6.0">6.0</SelectItem></SelectContent>
                        </Select>
                      </div>
                      {form.isBilateral ? (
                        <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                          <div>
                            <Label className={`${labelClass} ${isChanged('ecouteur_puissance') ? 'text-red-600 font-bold' : ''}`}>Puissance OD *</Label>
                            <Select value={form.ecouteurPuissance} onValueChange={form.setEcouteurPuissance}>
                              <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_puissance')}`} data-testid="puissance-od"><SelectValue placeholder="OD" /></SelectTrigger>
                              <SelectContent className="bg-white"><SelectItem value="S">S (Standard)</SelectItem><SelectItem value="M">M (Medium)</SelectItem><SelectItem value="P">P (Power)</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className={labelClass}>Puissance OG *</Label>
                            <Select value={form.ecouteurPuissanceOG} onValueChange={form.setEcouteurPuissanceOG}>
                              <SelectTrigger className="rounded-sm bg-white" data-testid="puissance-og"><SelectValue placeholder="OG" /></SelectTrigger>
                              <SelectContent className="bg-white"><SelectItem value="S">S (Standard)</SelectItem><SelectItem value="M">M (Medium)</SelectItem><SelectItem value="P">P (Power)</SelectItem></SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className={`${labelClass} ${isChanged('ecouteur_puissance') ? 'text-red-600 font-bold' : ''}`}>Puissance écouteur *</Label>
                          <Select value={form.ecouteurPuissance} onValueChange={form.setEcouteurPuissance}>
                            <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_puissance')}`}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                            <SelectContent className="bg-white"><SelectItem value="S">S (Standard)</SelectItem><SelectItem value="M">M (Medium)</SelectItem><SelectItem value="P">P (Power)</SelectItem></SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}

                  {/* Oticon group */}
                  {form.marque && isOticonGroup && (
                    <>
                      <div>
                        <Label className={`${labelClass} ${isChanged('ecouteur_type') ? 'text-red-600 font-bold' : ''}`}>Type d'écouteur *</Label>
                        <Select value={form.ecouteurType} onValueChange={form.setEcouteurType}>
                          <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_type')}`}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="minifit">Minifit (anciennes générations)</SelectItem>
                            <SelectItem value="minifit_detect">Minifit Detect (nouvelles générations)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {form.isBilateral ? (
                        <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                          <div>
                            <Label className={`${labelClass} ${isChanged('ecouteur_puissance_oticon') ? 'text-red-600 font-bold' : ''}`}>Puissance OD *</Label>
                            <Select value={form.ecouteurPuissanceOticon} onValueChange={form.setEcouteurPuissanceOticon}>
                              <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_puissance_oticon')}`} data-testid="puissance-oticon-od"><SelectValue placeholder="OD" /></SelectTrigger>
                              <SelectContent className="bg-white"><SelectItem value="65">65</SelectItem><SelectItem value="85">85</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className={labelClass}>Puissance OG *</Label>
                            <Select value={form.ecouteurPuissanceOticonOG} onValueChange={form.setEcouteurPuissanceOticonOG}>
                              <SelectTrigger className="rounded-sm bg-white" data-testid="puissance-oticon-og"><SelectValue placeholder="OG" /></SelectTrigger>
                              <SelectContent className="bg-white"><SelectItem value="65">65</SelectItem><SelectItem value="85">85</SelectItem></SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className={`${labelClass} ${isChanged('ecouteur_puissance_oticon') ? 'text-red-600 font-bold' : ''}`}>Puissance écouteur *</Label>
                          <Select value={form.ecouteurPuissanceOticon} onValueChange={form.setEcouteurPuissanceOticon}>
                            <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_puissance_oticon')}`}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                            <SelectContent className="bg-white"><SelectItem value="65">65</SelectItem><SelectItem value="85">85</SelectItem></SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}

                  {/* WSA group (Signia / Rexton / Biotone / Widex) : écouteur 2.0 ou 3.0 + puissance S/M/P */}
                  {form.marque && isWSAGroup && (
                    <>
                      <div>
                        <Label className={`${labelClass} ${isChanged('ecouteur_version_wsa') ? 'text-red-600 font-bold' : ''}`}>Écouteur *</Label>
                        <RadioGroup value={form.ecouteurVersionWSA} onValueChange={form.setEcouteurVersionWSA} className="flex gap-2" data-testid="ecouteur-wsa-version-group">
                          <label className={`flex items-center gap-2 py-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.ecouteurVersionWSA === '2.0' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                            <RadioGroupItem value="2.0" id={id('ecouteur-wsa-20')} data-testid="ecouteur-wsa-20" />
                            <span className="text-sm">2.0</span>
                          </label>
                          <label className={`flex items-center gap-2 py-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.ecouteurVersionWSA === '3.0' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                            <RadioGroupItem value="3.0" id={id('ecouteur-wsa-30')} data-testid="ecouteur-wsa-30" />
                            <span className="text-sm">3.0</span>
                          </label>
                        </RadioGroup>
                      </div>
                      {form.isBilateral ? (
                        <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                          <div>
                            <Label className={`${labelClass} ${isChanged('ecouteur_puissance_wsa') ? 'text-red-600 font-bold' : ''}`}>Puissance OD *</Label>
                            <RadioGroup value={form.ecouteurPuissanceWSA} onValueChange={form.setEcouteurPuissanceWSA} className="flex gap-2" data-testid="puissance-wsa-od-group">
                              {['S', 'M', 'P'].map(p => (
                                <label key={p} className={`flex items-center gap-1.5 py-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.ecouteurPuissanceWSA === p ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                                  <RadioGroupItem value={p} id={id(`puissance-wsa-od-${p}`)} data-testid={`puissance-wsa-od-${p}`} />
                                  <span className="text-sm">{p}</span>
                                </label>
                              ))}
                            </RadioGroup>
                          </div>
                          <div>
                            <Label className={labelClass}>Puissance OG *</Label>
                            <RadioGroup value={form.ecouteurPuissanceWSAOG} onValueChange={form.setEcouteurPuissanceWSAOG} className="flex gap-2" data-testid="puissance-wsa-og-group">
                              {['S', 'M', 'P'].map(p => (
                                <label key={p} className={`flex items-center gap-1.5 py-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.ecouteurPuissanceWSAOG === p ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                                  <RadioGroupItem value={p} id={id(`puissance-wsa-og-${p}`)} data-testid={`puissance-wsa-og-${p}`} />
                                  <span className="text-sm">{p}</span>
                                </label>
                              ))}
                            </RadioGroup>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className={`${labelClass} ${isChanged('ecouteur_puissance_wsa') ? 'text-red-600 font-bold' : ''}`}>Puissance écouteur *</Label>
                          <RadioGroup value={form.ecouteurPuissanceWSA} onValueChange={form.setEcouteurPuissanceWSA} className="flex gap-2" data-testid="puissance-wsa-group">
                            {['S', 'M', 'P'].map(p => (
                              <label key={p} className={`flex items-center gap-1.5 py-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.ecouteurPuissanceWSA === p ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                                <RadioGroupItem value={p} id={id(`puissance-wsa-${p}`)} data-testid={`puissance-wsa-${p}`} />
                                <span className="text-sm">{p}</span>
                              </label>
                            ))}
                          </RadioGroup>
                        </div>
                      )}
                    </>
                  )}

                  {/* Other brands (hors Phonak, Oticon, WSA) : champ libre */}
                  {form.marque && !isPhonakGroup && !isOticonGroup && !isWSAGroup && (
                    <div className="sm:col-span-2">
                      <Label className={labelClass}>Information sur l'écouteur *{form.isBilateral ? ' (préciser OD/OG si puissances différentes)' : ''}</Label>
                      <Input placeholder="Précisez le modèle et la puissance de l'écouteur..." value={form.ecouteurInfoComplementaire} onChange={(e) => form.setEcouteurInfoComplementaire(e.target.value)} className="rounded-sm" />
                    </div>
                  )}
                </>
              )}

              {form.emboutType === 'bte' && (
                <>
                  <div>
                    <Label className={`${labelClass} ${isChanged('marque') ? 'text-red-600 font-bold' : ''}`}>Marque de l'appareil</Label>
                    <Select value={form.marque} onValueChange={form.setMarque}>
                      <SelectTrigger className={`rounded-sm bg-white ${changedBorder('marque')}`} data-testid="marque-select"><SelectValue placeholder="Sélectionner (optionnel)" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {MARQUES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500 mt-1">Permet d'activer les options spécifiques à la marque (Power SlimTube, Corda MiniFit, etc.).</p>
                  </div>
                  <div className="bg-zinc-50 p-3 sm:p-4 rounded-sm border border-zinc-200">
                    <p className="text-sm text-zinc-600">Pour un embout BTE (contour d'oreille), aucune information détaillée sur l'appareil n'est nécessaire — seule la marque (optionnelle) permet d'activer les options spécifiques marque.</p>
                  </div>
                </>
              )}

              {form.emboutType === 'silk' && (
                <div className="sm:col-span-2 bg-blue-50 p-3 sm:p-4 rounded-sm border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium">Embout intra-auriculaire</p>
                  <p className="text-sm text-blue-600 mt-1">Compatible Signia SILK et Rexton INOX. L'option de montage Intra Silk sera automatiquement proposée.</p>
                </div>
              )}

              {isIntegral && (
                <div className="sm:col-span-2 grid gap-3" data-testid="integral-section">
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-sm border border-blue-200">
                    <p className="text-sm text-blue-700 font-medium">Embout intégral — écouteur intégré</p>
                    <p className="text-sm text-blue-600 mt-1">
                      L'embout intégral est pour l'instant uniquement disponible pour les appareils <strong>Phonak</strong>.
                      L'écouteur sera intégré à l'embout sur mesure du patient. Merci de sélectionner la version,
                      la puissance de l'écouteur et la longueur du câble.
                    </p>
                    <p className="text-xs text-blue-700 mt-2">Délai de fabrication estimé : <strong>5 jours ouvrés</strong>.</p>
                  </div>

                  {/* Filtre choice for Intégral */}
                  <div className="bg-white p-3 rounded-sm border border-zinc-200" data-testid="integral-filtre-section">
                    <Label className={`${labelClass} mb-2`}>Filtre / parcerumen *</Label>
                    <RadioGroup value={form.filtreType || 'cerustop'} onValueChange={form.setFiltreType} className="flex flex-wrap gap-2">
                      <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors ${(form.filtreType || 'cerustop') === 'cerustop' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                        <RadioGroupItem value="cerustop" id="integral-filtre-cerustop" data-testid="integral-filtre-cerustop" />
                        <span className="font-medium text-sm">Montage CeruStop</span>
                      </label>
                      <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors ${form.filtreType === 'hf4' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                        <RadioGroupItem value="hf4" id="integral-filtre-hf4" data-testid="integral-filtre-hf4" />
                        <span className="font-medium text-sm">Montage HF4</span>
                      </label>
                    </RadioGroup>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className={`${labelClass} ${isChanged('ecouteur_version') ? 'text-red-600 font-bold' : ''}`}>Version écouteur *</Label>
                      <Select value={form.ecouteurVersion} onValueChange={(v) => {
                        form.setEcouteurVersion(v);
                        // Reset cable length if "00" was selected and we switch to 5.0
                        if (v === '5.0' && form.integralCableLength === '00') {
                          form.setIntegralCableLength('');
                        }
                      }}>
                        <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_version')}`} data-testid="integral-version-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {INTEGRAL_VERSIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={`${labelClass} ${isChanged('ecouteur_puissance') ? 'text-red-600 font-bold' : ''}`}>Puissance écouteur *</Label>
                      <Select value={form.ecouteurPuissance} onValueChange={form.setEcouteurPuissance}>
                        <SelectTrigger className={`rounded-sm bg-white ${changedBorder('ecouteur_puissance')}`} data-testid="integral-puissance-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {INTEGRAL_PUISSANCES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={`${labelClass} ${isChanged('integral_cable_length') ? 'text-red-600 font-bold' : ''}`}>Longueur câble *</Label>
                      <Select value={form.integralCableLength} onValueChange={form.setIntegralCableLength} disabled={!form.ecouteurVersion}>
                        <SelectTrigger className={`rounded-sm bg-white ${changedBorder('integral_cable_length')}`} data-testid="integral-cable-select"><SelectValue placeholder={form.ecouteurVersion ? 'Sélectionner' : 'Choisir version'} /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {cableOptions.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              <div className="sm:col-span-2">
                <Label className={labelClass}>Information complémentaire</Label>
                <Input placeholder="Informations sur l'appareil ou la commande..." value={form.infoComplementaire} onChange={(e) => form.setInfoComplementaire(e.target.value)} className="rounded-sm" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Montage (RIC: brand-dependent. BTE silicone: tube vs tube lock. BTE resin: tube only) */}
      {form.fabricationType === 'embout_auditif' && isRIC && form.marque && (
        <div className={sectionClass} data-testid="montage-section">
          <H className={headingClass}>Options de montage</H>
          <RadioGroup value={form.montageType} onValueChange={form.setMontageType} className="space-y-2">
            {isPhonakGroup && (
              <>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'slim_tip_link' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="slim_tip_link" id={id('montage-slim')} data-testid="montage-slim-tip" />
                  <span className="font-medium text-sm">SlimTip Link</span>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_tube' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="montage_tube" id={id('montage-tube-phonak')} data-testid="montage-tube" />
                  <span className="font-medium text-sm">Click tube <span className="text-xs text-zinc-500 font-normal">(montage à réaliser par le client)</span></span>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_bague_3dsa' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="montage_bague_3dsa" id={id('montage-bague-3dsa')} data-testid="montage-bague-3dsa" />
                  <span className="font-medium text-sm">Bague 3DSA</span>
                </label>
              </>
            )}
            {isOticonGroup && (
              <>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_bague_prodition' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="montage_bague_prodition" id={id('montage-bague-pro')} data-testid="montage-bague-prodition" />
                  <span className="font-medium text-sm">{MONTAGE_LABELS.montage_bague_prodition}</span>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_tube' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="montage_tube" id={id('montage-tube-oticon')} data-testid="montage-tube" />
                  <span className="font-medium text-sm">Montage tube</span>
                </label>
              </>
            )}
            {isWSAGroup && (
              <>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_bague_wsa' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="montage_bague_wsa" id={id('montage-bague-wsa')} data-testid="montage-bague-wsa" />
                  <span className="font-medium text-sm">{MONTAGE_LABELS.montage_bague_wsa}</span>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_tube' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="montage_tube" id={id('montage-tube-wsa')} data-testid="montage-tube" />
                  <span className="font-medium text-sm">Montage tube</span>
                </label>
              </>
            )}
            {!isPhonakGroup && !isOticonGroup && !isWSAGroup && (
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors border-zinc-900 bg-zinc-50`}>
                <RadioGroupItem value="montage_tube" id={id('montage-tube-other')} data-testid="montage-tube" />
                <span className="font-medium text-sm">Montage tube</span>
              </label>
            )}
          </RadioGroup>

          {/* Filtre choice for SlimTip Link (Phonak/Unitron/Hansaton) */}
          {isPhonakGroup && form.montageType === 'slim_tip_link' && (
            <div className="mt-3 p-3 bg-zinc-50 rounded-sm border border-zinc-200" data-testid="slimtip-filtre-section">
              <Label className="label-overline mb-2 block">Filtre / parcerumen *</Label>
              <RadioGroup value={form.filtreType || 'cerustop'} onValueChange={form.setFiltreType} className="flex flex-wrap gap-2">
                <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${(form.filtreType || 'cerustop') === 'cerustop' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="cerustop" id={id('slimtip-filtre-cerustop')} data-testid="slimtip-filtre-cerustop" />
                  <span className="font-medium text-sm">Montage CeruStop</span>
                </label>
                <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.filtreType === 'hf4' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
                  <RadioGroupItem value="hf4" id={id('slimtip-filtre-hf4')} data-testid="slimtip-filtre-hf4" />
                  <span className="font-medium text-sm">Montage HF4</span>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* Bague 3DSA (Phonak RIC uniquement) : consentement + option de repli */}
          {isPhonakGroup && form.montageType === 'montage_bague_3dsa' && (
            <div className="mt-3 p-3 bg-amber-50 rounded-sm border border-amber-300" data-testid="bague-3dsa-consent-section">
              <div className="flex items-start gap-2 mb-3">
                <input
                  type="checkbox"
                  id={id('bague-3dsa-consent')}
                  data-testid="bague-3dsa-consent-checkbox"
                  className="mt-1"
                  checked={!!form.bague3dsaConsent}
                  onChange={(e) => {
                    form.setBague3dsaConsent(e.target.checked);
                    if (!e.target.checked) form.setBague3dsaFallback('');
                  }}
                />
                <label htmlFor={id('bague-3dsa-consent')} className="text-sm text-amber-900 cursor-pointer">
                  J&apos;autorise le technicien à changer d&apos;option de montage si la bague 3DSA ne passe pas dans le conduit.
                </label>
              </div>
              {form.bague3dsaConsent && (
                <div className="pl-6" data-testid="bague-3dsa-fallback-section">
                  <Label className="label-overline mb-2 block">Option de repli souhaitée *</Label>
                  <RadioGroup value={form.bague3dsaFallback} onValueChange={form.setBague3dsaFallback} className="flex flex-wrap gap-2">
                    <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.bague3dsaFallback === 'slim_tip_link' ? 'border-amber-900' : 'border-amber-200 hover:border-amber-500'}`}>
                      <RadioGroupItem value="slim_tip_link" id={id('bague-3dsa-fallback-slim')} data-testid="bague-3dsa-fallback-slim" />
                      <span className="font-medium text-sm">SlimTip Link</span>
                    </label>
                    <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.bague3dsaFallback === 'montage_tube' ? 'border-amber-900' : 'border-amber-200 hover:border-amber-500'}`}>
                      <RadioGroupItem value="montage_tube" id={id('bague-3dsa-fallback-tube')} data-testid="bague-3dsa-fallback-tube" />
                      <span className="font-medium text-sm">Click tube</span>
                    </label>
                  </RadioGroup>
                </div>
              )}
            </div>
          )}

          {form.montageType && MONTAGE_MESSAGES[form.montageType] && (
            <div className="flex items-start gap-3 mt-2">
              <div className="flex-1">
                <OrangeInfoBox>{MONTAGE_MESSAGES[form.montageType]}</OrangeInfoBox>
              </div>
              <ImagePlaceholder label={form.montageType} />
            </div>
          )}
        </div>
      )}

      {/* Montage BTE moved to FormFormeMatiere (after material selection) */}
    </>
  );
}
